<template>
  <div>
    <DxTabs
      class="balances-tabs"
      :selected-index="selectedIndex"
      :items="viewTabs"
      :key-expr="'id'"
      :width="'auto'"
      @itemClick="onTabClick"
    />

    <div v-if="summary.StopCredit === 'Y'" class="sc-banner">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round">
        <path d="M6 1L11 10.5H1L6 1z" />
        <line x1="6" y1="5" x2="6" y2="7.5" />
        <circle cx="6" cy="9.2" r=".6" fill="currentColor" stroke="none" />
      </svg>
      <span>Stop Credit — credit limit of {{ formattedCreditLimit }} exceeded</span>
    </div>

    <DxDataGrid
      :key="selectedIndex"
      :data-source="visibleBuckets"
      :show-borders="true"
      :show-column-lines="true"
      :show-row-lines="true"
      :column-auto-width="true"
      :columns="columns"
      :paging="{ enabled: false }"
      :pager="{ visible: false }"
      :column-chooser="{ enabled: false }"
      :search-panel="{ visible: false }"
      @row-click="onRowClick"
      @cell-prepared="onCellPrepared"
    >
      <DxSummary>
        <DxTotalItem
          column="DisplayLabel"
          summary-type="count"
          display-format="Total"
        />
        <DxTotalItem
          column="BaseAmount"
          summary-type="sum"
          :value-format="decimalFormat"
          display-format="{0}"
        />
        <DxTotalItem
          column="FxAmount"
          summary-type="sum"
          :value-format="decimalFormat"
          display-format="{0}"
        />
        <DxTotalItem
          column="InvoiceCount"
          summary-type="sum"
          display-format="{0}"
        />
      </DxSummary>
    </DxDataGrid>
  </div>
</template>

<script>
import { DxDataGrid, DxSummary, DxTotalItem } from "devextreme-vue/data-grid";
import { getDecimalFormat } from "../../_store/helpers";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";

const BUCKET_TERM_KEYS = ["Current", "Period1", "Period2", "Period3", "Period4"];
const OVERDUE_COLUMNS = ["BaseAmount", "FxAmount", "InvoiceCount"];

export default {
  name: "GcDashboardCustomerBalancesCard",
  components: { DxDataGrid, DxSummary, DxTotalItem },
  props: {
    summary: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedIndex: 0,
      viewTabs: [
        { id: 0, text: "Age By Terms" },
        { id: 1, text: "Age By Period" },
      ],
    };
  },
  computed: {
    decimalFormat() {
      return getDecimalFormat("Decimal-16-2").ReadOnlyFormat;
    },
    formattedCreditLimit() {
      return `$${Number(this.summary.CreditLimit || 0).toLocaleString("en-AU", {
        maximumFractionDigits: 0,
      })}`;
    },
    columns() {
      return [
        { caption: "Bucket", dataField: "DisplayLabel" },
        {
          caption: `Total (${this.summary.BaseCurrencyCode})`,
          dataField: "BaseAmount",
          alignment: "right",
          format: this.decimalFormat,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: `Total (${this.summary.FxCurrencyCode})`,
          dataField: "FxAmount",
          alignment: "right",
          format: this.decimalFormat,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: "Count",
          dataField: "InvoiceCount",
          alignment: "right",
          width: ColumnWidth.ItemCount.width,
          minWidth: ColumnWidth.ItemCount.minWidth,
        },
      ];
    },
    activeTermType() {
      return this.selectedIndex === 0 ? "Terms" : "Period";
    },
    activeBuckets() {
      return this.selectedIndex === 0
        ? this.summary.AgingByTerms
        : this.summary.AgingByPeriod;
    },
    labeledBuckets() {
      return this.activeBuckets.map((bucket, index) => ({
        ...bucket,
        OriginalIndex: index,
        DisplayLabel:
          this.selectedIndex === 0
            ? this.formatTermsLabel(bucket, index)
            : this.formatPeriodLabel(index),
      }));
    },
    visibleBuckets() {
      if (this.selectedIndex !== 0 || this.summary.CustomerTerms !== 0) {
        return this.labeledBuckets;
      }

      return this.labeledBuckets.filter(
        (bucket) =>
          !(bucket.Label === "N/A" && bucket.BaseAmount === 0 && bucket.FxAmount === 0)
      );
    },
  },
  methods: {
    onTabClick(e) {
      this.selectedIndex = e.itemIndex;
    },
    formatTermsLabel(bucket, index) {
      const terms = this.summary.CustomerTerms;

      if (terms === 0) {
        return bucket.Label;
      }
      if (index === 0) {
        return "Current";
      }
      if (index === 4) {
        return `${terms * 3}+ Days`;
      }

      return `${terms * index} Days`;
    },
    formatPeriodLabel(index) {
      const asOfDate = new Date(this.summary.AsOfDate);
      const monthsBack = index < 4 ? index : 3;
      const target = new Date(asOfDate.getFullYear(), asOfDate.getMonth() - monthsBack, 1);
      const monthYear = target.toLocaleDateString("en-AU", {
        month: "short",
        year: "numeric",
      });

      return index < 4 ? monthYear : `Pre-${monthYear}`;
    },
    onRowClick(e) {
      const term = BUCKET_TERM_KEYS[e.data.OriginalIndex];

      this.$emit("bucket-click", {
        termType: this.activeTermType,
        term,
        bucketLabel: e.data.DisplayLabel,
      });
    },
    onCellPrepared(e) {
      if (e.rowType !== "data" || !OVERDUE_COLUMNS.includes(e.column.dataField)) {
        return;
      }

      if (e.data.OriginalIndex !== 0 && e.data.BaseAmount > 0) {
        e.cellElement.classList.add("overdue-cell");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.balances-tabs {
  margin-bottom: 8px;
}

.sc-banner {
  background: #fff8f8;
  border: 1px solid #f5c6cb;
  color: #842029;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    flex-shrink: 0;
  }
}
</style>
